import React, { useEffect, useState, useRef } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';
import { getCompanyLogoUrl } from '../utils/imageUtils';
import XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function BankReport() {
    const { http, user } = AuthUser();
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState([]);
    const [totals, setTotals] = useState({});
    const [companyName, setCompanyName] = useState('');
    const [companyLogo, setCompanyLogo] = useState('');
    const [companyAddress, setCompanyAddress] = useState('');
    const printRef = useRef(null);

    useEffect(() => {
        fetchReport();
        fetchCompanyInfo();
    }, []);

    const fetchCompanyInfo = async () => {
        try {
            const response = await http.get(`/companyinfo?login_user=${user.id}`);
            if (response.data.data && response.data.data.length > 0) {
                setCompanyName(response.data.data[0].company_name || '');
                setCompanyLogo(response.data.data[0].company_logo || '');
                setCompanyAddress(response.data.data[0].company_address || '');
            }
        } catch (error) {
            console.error('Error fetching company info:', error);
        }
    };

    const fetchReport = async () => {
        setLoading(true);
        try {
            const response = await http.get(`/salary_report/bank?login_user=${user.id}`);
            if (response.data.success) {
                setReportData(response.data.data);
                setTotals(response.data.totals || {});
            }
        } catch (error) {
            console.error('Error fetching Bank report:', error);
            toast.error('Error loading Bank report');
        } finally {
            setLoading(false);
        }
    };

    const fmt = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount || 0);

    const headers = ['Sl.No', 'Name', 'Designation', 'Emp Type', 'Department', 'Account Number', 'IFSC Code', 'PF Account', 'ESI Number', 'Gross Salary', 'Net Salary'];

    const buildRows = () => reportData.map(r => [r.sl_no, r.name, r.designation || '-', r.emp_type || '-', r.department || '-', r.account_num || '-', r.bank_ifsc || '-', r.pf_acc_num || '-', r.esi_number || '-', r.gross_salary || 0, r.net_salary || 0]);

    const totalRow = () => ['', '', '', '', 'Total', '', '', '', '', totals.gross_salary || 0, totals.net_salary || 0];

    // ===== BULK exports (all employees) =====

    const styleSheet = (ws, colCount, rowCount) => {
        for (let c = 0; c < colCount; c++) {
            const addr = XLSX.utils.encode_cell({ r: 0, c });
            if (ws[addr]) ws[addr].s = { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 }, fill: { fgColor: { rgb: '1A237E' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: { top: { style: 'thin', color: { rgb: '1A237E' } }, bottom: { style: 'thin', color: { rgb: '1A237E' } }, left: { style: 'thin', color: { rgb: '1A237E' } }, right: { style: 'thin', color: { rgb: '1A237E' } } } };
        }
        for (let r = 1; r <= rowCount; r++) {
            const isTotal = r === rowCount;
            const isEven = r % 2 === 0;
            for (let c = 0; c < colCount; c++) {
                const addr = XLSX.utils.encode_cell({ r, c });
                if (!ws[addr]) continue;
                const isNum = typeof ws[addr].v === 'number';
                ws[addr].s = {
                    font: { sz: 10, bold: isTotal, color: { rgb: isTotal ? '1A237E' : '333333' } },
                    fill: { fgColor: { rgb: isTotal ? 'E8EAF6' : isEven ? 'F0F0FF' : 'FFFFFF' } },
                    alignment: { horizontal: isNum ? 'right' : (c === 0 ? 'center' : 'left'), vertical: 'center' },
                    border: { top: { style: 'thin', color: { rgb: isTotal ? '1A237E' : 'DDDDDD' } }, bottom: { style: 'thin', color: { rgb: 'DDDDDD' } }, left: { style: 'thin', color: { rgb: 'DDDDDD' } }, right: { style: 'thin', color: { rgb: 'DDDDDD' } } },
                    ...(isNum ? { numFmt: '#,##0.00' } : {})
                };
            }
        }
    };

    const handleExcel = () => {
        if (reportData.length === 0) return;
        const rows = buildRows();
        const data = [headers, ...rows, totalRow()];
        const ws = XLSX.utils.aoa_to_sheet(data);
        ws['!cols'] = headers.map((h, i) => ({ wch: [1, 4].includes(i) ? 22 : [5, 6, 7, 8].includes(i) ? 18 : 14 }));
        ws['!rows'] = [{ hpt: 22 }];
        styleSheet(ws, headers.length, rows.length + 1);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Bank Report');
        XLSX.writeFile(wb, `Bank_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const handlePdf = () => {
        if (reportData.length === 0) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        doc.setFontSize(16); doc.setTextColor(26, 35, 126);
        doc.text(companyName || 'Company', doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });
        if (companyAddress) { doc.setFontSize(9); doc.setTextColor(100); doc.text(companyAddress, doc.internal.pageSize.getWidth() / 2, 21, { align: 'center' }); }
        doc.setFontSize(12); doc.setTextColor(255); doc.setFillColor(26, 35, 126);
        doc.rect(14, 25, doc.internal.pageSize.getWidth() - 28, 8, 'F');
        doc.text('BANK REPORT', doc.internal.pageSize.getWidth() / 2, 30.5, { align: 'center' });
        const rows = buildRows(); rows.push(totalRow());
        autoTable(doc, {
            head: [headers], body: rows, startY: 37, theme: 'grid',
            headStyles: { fillColor: [26, 35, 126], textColor: 255, fontSize: 7, halign: 'center' },
            bodyStyles: { fontSize: 7 },
            columnStyles: { 0: { halign: 'center' }, 9: { halign: 'right' }, 10: { halign: 'right' } },
            didParseCell: (data) => { if (data.row.index === rows.length - 1) { data.cell.styles.fontStyle = 'bold'; data.cell.styles.fillColor = [232, 234, 246]; } }
        });
        doc.save(`Bank_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const handleCsvDownload = () => {
        if (reportData.length === 0) return;
        const rows = buildRows().map(r => [r[0], ...r.slice(1, 9).map(v => `"${(v || '').toString().replace(/"/g, '""')}"`), r[9], r[10]]);
        rows.push(totalRow());
        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a'); link.href = url;
        link.setAttribute('download', `Bank_Report_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
    };

    const handlePrint = () => {
        const printContent = printRef.current;
        const win = window.open('', '_blank');
        win.document.write(`<!DOCTYPE html><html><head><title>Bank Report - ${companyName}</title><style>
            body{font-family:Arial,sans-serif;margin:0;padding:20px;color:#000}
            .report-header{text-align:center;border-bottom:3px solid #1a237e;padding-bottom:15px;margin-bottom:20px}
            .company-name{font-size:24px;font-weight:bold;color:#1a237e;text-transform:uppercase}
            table{width:100%;border-collapse:collapse}
            th{background:#1a237e;color:#fff;padding:8px 10px;font-size:11px;text-align:center;border:1px solid #1a237e}
            td{padding:6px 10px;font-size:11px;border:1px solid #ddd}
            .text-end{text-align:right}.text-center{text-align:center}
            .no-print{display:none!important}
            @media print{@page{margin:10mm;size:A4 landscape}}
        </style></head><body>${printContent.innerHTML}</body></html>`);
        win.document.close(); win.focus(); win.onafterprint = () => win.close(); win.print();
    };

    // ===== INDIVIDUAL employee exports =====

    const buildIndividualHtml = (row) => {
        const logoHtml = companyLogo ? `<img src="${getCompanyLogoUrl(companyLogo)}" style="width:50px;height:40px;object-fit:contain;margin-right:12px" />` : '';
        return `
        <div style="font-family:Arial,sans-serif;max-width:700px;margin:0 auto;padding:30px">
            <div style="text-align:center;border-bottom:3px solid #1a237e;padding-bottom:15px;margin-bottom:25px">
                <div style="display:flex;align-items:center;justify-content:center;gap:10px">
                    ${logoHtml}
                    <div>
                        <div style="font-size:22px;font-weight:bold;color:#1a237e;text-transform:uppercase;letter-spacing:1px">${companyName}</div>
                        ${companyAddress ? `<div style="font-size:11px;color:#666;margin-top:3px">${companyAddress}</div>` : ''}
                    </div>
                </div>
            </div>
            <div style="background:#1a237e;color:#fff;text-align:center;padding:8px;font-size:14px;font-weight:bold;letter-spacing:2px;border-radius:4px;margin-bottom:25px">
                EMPLOYEE BANK DETAILS
            </div>
            <table style="width:100%;border-collapse:collapse;margin-bottom:20px">
                <tr><td style="padding:10px 14px;border:1px solid #ddd;background:#f8f9fa;font-weight:bold;width:35%;font-size:12px">Employee Name</td><td style="padding:10px 14px;border:1px solid #ddd;font-size:13px;font-weight:bold;color:#1a237e">${row.name}</td></tr>
                <tr><td style="padding:10px 14px;border:1px solid #ddd;background:#f8f9fa;font-weight:bold;font-size:12px">Designation</td><td style="padding:10px 14px;border:1px solid #ddd;font-size:12px">${row.designation || '-'}</td></tr>
                <tr><td style="padding:10px 14px;border:1px solid #ddd;background:#f8f9fa;font-weight:bold;font-size:12px">Employee Type</td><td style="padding:10px 14px;border:1px solid #ddd;font-size:12px">${row.emp_type || '-'}</td></tr>
                <tr><td style="padding:10px 14px;border:1px solid #ddd;background:#f8f9fa;font-weight:bold;font-size:12px">Department</td><td style="padding:10px 14px;border:1px solid #ddd;font-size:12px">${row.department || '-'}</td></tr>
            </table>
            <div style="background:#e8eaf6;padding:8px 14px;font-weight:bold;font-size:13px;color:#1a237e;border-radius:4px 4px 0 0;border:1px solid #c5cae9">Bank & Statutory Details</div>
            <table style="width:100%;border-collapse:collapse;margin-bottom:20px">
                <tr><td style="padding:10px 14px;border:1px solid #ddd;background:#f8f9fa;font-weight:bold;width:35%;font-size:12px">Bank Account Number</td><td style="padding:10px 14px;border:1px solid #ddd;font-size:13px;font-family:monospace;letter-spacing:1px;font-weight:bold">${row.account_num || '-'}</td></tr>
                <tr><td style="padding:10px 14px;border:1px solid #ddd;background:#f8f9fa;font-weight:bold;font-size:12px">IFSC Code</td><td style="padding:10px 14px;border:1px solid #ddd;font-size:13px;font-family:monospace;letter-spacing:1px;font-weight:bold">${row.bank_ifsc || '-'}</td></tr>
                <tr><td style="padding:10px 14px;border:1px solid #ddd;background:#f8f9fa;font-weight:bold;font-size:12px">PF Account Number</td><td style="padding:10px 14px;border:1px solid #ddd;font-size:12px">${row.pf_acc_num || '-'}</td></tr>
                <tr><td style="padding:10px 14px;border:1px solid #ddd;background:#f8f9fa;font-weight:bold;font-size:12px">ESI Number</td><td style="padding:10px 14px;border:1px solid #ddd;font-size:12px">${row.esi_number || '-'}</td></tr>
            </table>
            <div style="background:#e8f5e9;padding:8px 14px;font-weight:bold;font-size:13px;color:#2e7d32;border-radius:4px 4px 0 0;border:1px solid #a5d6a7">Salary Details</div>
            <table style="width:100%;border-collapse:collapse">
                <tr><td style="padding:10px 14px;border:1px solid #ddd;background:#f8f9fa;font-weight:bold;width:35%;font-size:12px">Gross Salary</td><td style="padding:10px 14px;border:1px solid #ddd;font-size:13px;font-weight:bold">${fmt(row.gross_salary)}</td></tr>
                <tr><td style="padding:10px 14px;border:1px solid #ddd;background:#f8f9fa;font-weight:bold;font-size:12px">Net Salary</td><td style="padding:10px 14px;border:1px solid #ddd;font-size:14px;font-weight:bold;color:#2e7d32">${fmt(row.net_salary)}</td></tr>
            </table>
            <div style="margin-top:30px;padding-top:15px;border-top:1px solid #ddd;text-align:center;color:#999;font-size:10px">
                Generated on ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
            </div>
        </div>`;
    };

    const handleIndividualPrint = (row) => {
        const win = window.open('', '_blank');
        win.document.write(`<!DOCTYPE html><html><head><title>Bank Details - ${row.name}</title>
            <style>body{margin:0;padding:0}@media print{@page{margin:15mm;size:A4}}</style>
        </head><body>${buildIndividualHtml(row)}</body></html>`);
        win.document.close(); win.focus(); win.onafterprint = () => win.close(); win.print();
    };

    const handleIndividualPdf = (row) => {
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const w = doc.internal.pageSize.getWidth();
        let y = 15;

        doc.setFontSize(16); doc.setTextColor(26, 35, 126);
        doc.text(companyName || 'Company', w / 2, y, { align: 'center' }); y += 6;
        if (companyAddress) { doc.setFontSize(9); doc.setTextColor(100); doc.text(companyAddress, w / 2, y, { align: 'center' }); y += 5; }
        doc.setDrawColor(26, 35, 126); doc.setLineWidth(0.8); doc.line(14, y, w - 14, y); y += 8;

        doc.setFillColor(26, 35, 126); doc.rect(14, y, w - 28, 8, 'F');
        doc.setFontSize(11); doc.setTextColor(255);
        doc.text('EMPLOYEE BANK DETAILS', w / 2, y + 5.5, { align: 'center' }); y += 14;

        const detailRows = [
            ['Employee Name', row.name],
            ['Designation', row.designation || '-'],
            ['Employee Type', row.emp_type || '-'],
            ['Department', row.department || '-'],
        ];
        autoTable(doc, {
            body: detailRows, startY: y, theme: 'grid',
            columnStyles: { 0: { fontStyle: 'bold', fillColor: [248, 249, 250], cellWidth: 55 } },
            bodyStyles: { fontSize: 10 }, margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 6;

        doc.setFillColor(232, 234, 246); doc.rect(14, y, w - 28, 7, 'F');
        doc.setFontSize(10); doc.setTextColor(26, 35, 126);
        doc.text('Bank & Statutory Details', 18, y + 5); y += 9;

        const bankRows = [
            ['Bank Account Number', row.account_num || '-'],
            ['IFSC Code', row.bank_ifsc || '-'],
            ['PF Account Number', row.pf_acc_num || '-'],
            ['ESI Number', row.esi_number || '-'],
        ];
        autoTable(doc, {
            body: bankRows, startY: y, theme: 'grid',
            columnStyles: { 0: { fontStyle: 'bold', fillColor: [248, 249, 250], cellWidth: 55 } },
            bodyStyles: { fontSize: 10 }, margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 6;

        doc.setFillColor(232, 245, 233); doc.rect(14, y, w - 28, 7, 'F');
        doc.setFontSize(10); doc.setTextColor(46, 125, 50);
        doc.text('Salary Details', 18, y + 5); y += 9;

        const salaryRows = [
            ['Gross Salary', fmt(row.gross_salary)],
            ['Net Salary', fmt(row.net_salary)],
        ];
        autoTable(doc, {
            body: salaryRows, startY: y, theme: 'grid',
            columnStyles: { 0: { fontStyle: 'bold', fillColor: [248, 249, 250], cellWidth: 55 } },
            bodyStyles: { fontSize: 10 }, margin: { left: 14, right: 14 },
        });

        doc.setFontSize(8); doc.setTextColor(150);
        doc.text(`Generated on ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}`, w / 2, doc.lastAutoTable.finalY + 15, { align: 'center' });

        const safeName = (row.name || 'Employee').replace(/[^a-zA-Z0-9]/g, '_');
        doc.save(`Bank_Details_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const handleIndividualExcel = (row) => {
        const data = [
            ['Employee Bank Details'],
            [],
            ['Employee Name', row.name],
            ['Designation', row.designation || '-'],
            ['Employee Type', row.emp_type || '-'],
            ['Department', row.department || '-'],
            [],
            ['Bank & Statutory Details'],
            ['Bank Account Number', row.account_num || '-'],
            ['IFSC Code', row.bank_ifsc || '-'],
            ['PF Account Number', row.pf_acc_num || '-'],
            ['ESI Number', row.esi_number || '-'],
            [],
            ['Salary Details'],
            ['Gross Salary', row.gross_salary || 0],
            ['Net Salary', row.net_salary || 0],
        ];
        const ws = XLSX.utils.aoa_to_sheet(data);
        ws['!cols'] = [{ wch: 25 }, { wch: 30 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Bank Details');
        const safeName = (row.name || 'Employee').replace(/[^a-zA-Z0-9]/g, '_');
        XLSX.writeFile(wb, `Bank_Details_${safeName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-2 px-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0"><i className="fa fa-university me-2"></i>Bank Report</h5>
                    <div className="d-flex gap-1 align-items-center">
                        <button className="btn btn-sm btn-outline-success" onClick={handleExcel} disabled={reportData.length === 0} title="Download All - Excel">
                            <i className="fa fa-file-excel me-1"></i>Excel
                        </button>
                        <button className="btn btn-sm btn-outline-info" onClick={handleCsvDownload} disabled={reportData.length === 0} title="Download All - CSV">
                            <i className="fa fa-file-csv me-1"></i>CSV
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={handlePdf} disabled={reportData.length === 0} title="Download All - PDF">
                            <i className="fa fa-file-pdf me-1"></i>PDF
                        </button>
                        <button className="btn btn-sm btn-outline-dark" onClick={handlePrint} title="Print All">
                            <i className="fa fa-print me-1"></i>Print
                        </button>
                        <span className="badge bg-secondary ms-1">{reportData.length} employees</span>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-4"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                        ) : reportData.length === 0 ? (
                            <div className="text-center py-4 text-muted"><i className="fa fa-inbox fa-2x mb-2" style={{ opacity: 0.3 }}></i><p className="mb-0">No employee bank records found</p></div>
                        ) : (
                            <div ref={printRef}>
                                <div className="report-header d-none" style={{ textAlign: 'center', borderBottom: '3px solid #1a237e', paddingBottom: '15px', marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>
                                        {companyLogo && <img src={getCompanyLogoUrl(companyLogo)} alt="Logo" style={{ width: '60px', height: '50px', objectFit: 'contain' }} />}
                                        <div>
                                            <div className="company-name" style={{ fontSize: '24px', fontWeight: 'bold', color: '#1a237e', textTransform: 'uppercase' }}>{companyName}</div>
                                            {companyAddress && <div style={{ fontSize: '12px', color: '#555' }}>{companyAddress}</div>}
                                        </div>
                                    </div>
                                </div>
                                <div className="d-none" style={{ background: '#1a237e', color: '#fff', textAlign: 'center', padding: '8px', fontSize: '16px', fontWeight: 'bold', letterSpacing: '2px', marginBottom: '10px' }}>
                                    BANK REPORT
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-hover table-sm table-bordered mb-0">
                                        <thead style={{ backgroundColor: '#1a237e' }}>
                                            <tr>
                                                <th style={{ color: '#fff', fontSize: '12px', textAlign: 'center', width: '50px' }}>Sl.No</th>
                                                <th style={{ color: '#fff', fontSize: '12px' }}>Name</th>
                                                <th style={{ color: '#fff', fontSize: '12px' }}>Designation</th>
                                                <th style={{ color: '#fff', fontSize: '12px' }}>Emp Type</th>
                                                <th style={{ color: '#fff', fontSize: '12px' }}>Department</th>
                                                <th style={{ color: '#fff', fontSize: '12px' }}>Account Number</th>
                                                <th style={{ color: '#fff', fontSize: '12px' }}>IFSC Code</th>
                                                <th style={{ color: '#fff', fontSize: '12px' }}>PF Account</th>
                                                <th style={{ color: '#fff', fontSize: '12px' }}>ESI Number</th>
                                                <th style={{ color: '#fff', fontSize: '12px', textAlign: 'right' }}>Gross Salary</th>
                                                <th style={{ color: '#fff', fontSize: '12px', textAlign: 'right' }}>Net Salary</th>
                                                <th className="no-print" style={{ color: '#fff', fontSize: '12px', textAlign: 'center', width: '120px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportData.map((row) => (
                                                <tr key={row.sl_no}>
                                                    <td className="text-center" style={{ fontSize: '12px' }}>{row.sl_no}</td>
                                                    <td style={{ fontSize: '12px' }}><strong>{row.name}</strong></td>
                                                    <td style={{ fontSize: '11px' }}>{row.designation}</td>
                                                    <td style={{ fontSize: '11px' }}>{row.emp_type}</td>
                                                    <td style={{ fontSize: '11px' }}>{row.department}</td>
                                                    <td style={{ fontSize: '12px', fontFamily: 'monospace' }}>{row.account_num || '-'}</td>
                                                    <td style={{ fontSize: '12px', fontFamily: 'monospace' }}>{row.bank_ifsc || '-'}</td>
                                                    <td style={{ fontSize: '11px' }}>{row.pf_acc_num || '-'}</td>
                                                    <td style={{ fontSize: '11px' }}>{row.esi_number || '-'}</td>
                                                    <td className="text-end" style={{ fontSize: '12px' }}>{fmt(row.gross_salary)}</td>
                                                    <td className="text-end" style={{ fontSize: '12px', color: '#2e7d32', fontWeight: 'bold' }}>{fmt(row.net_salary)}</td>
                                                    <td className="text-center no-print">
                                                        <div className="btn-group btn-group-sm">
                                                            <button className="btn btn-outline-dark btn-sm" onClick={() => handleIndividualPrint(row)} title="Print">
                                                                <i className="fa fa-print"></i>
                                                            </button>
                                                            <button className="btn btn-outline-danger btn-sm" onClick={() => handleIndividualPdf(row)} title="PDF">
                                                                <i className="fa fa-file-pdf"></i>
                                                            </button>
                                                            <button className="btn btn-outline-success btn-sm" onClick={() => handleIndividualExcel(row)} title="Excel">
                                                                <i className="fa fa-file-excel"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr style={{ fontWeight: 'bold', background: '#e8eaf6', borderTop: '2px solid #1a237e' }}>
                                                <td colSpan="9" className="text-end" style={{ fontSize: '13px' }}>Total</td>
                                                <td className="text-end" style={{ fontSize: '13px' }}>{fmt(totals.gross_salary)}</td>
                                                <td className="text-end" style={{ fontSize: '13px', color: '#2e7d32' }}>{fmt(totals.net_salary)}</td>
                                                <td className="no-print"></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
